/**
 * builderDeclarationPdfExport.js
 * PDF export for the OC Submission "Builder's Declaration" form
 * (NSW Installation Certificate — Builder's Completion Certificate).
 *
 * Follows the app-wide jsPDF + jspdf-autotable + pdfShared.js conventions.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDefaultCompanyInfo, getLOGO_URL, LOGO_FALLBACK_URL } from './brandConfig';
import {
  NAVY, GOLD, WHITE, TEXT_COLOR, MID_GRAY,
  PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addWrappedText, addFooters, savePDF,
  renderNavyHeader, formatDate,
} from './pdfShared';

function sectionBar(doc, y, title) {
  y = ensureSpace(doc, y, 10);
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, y, CONTENT_WIDTH, 7, 'F');
  doc.setTextColor(...NAVY);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), PAGE.margin + 2, y + 5);
  return y + 7 + 3;
}

function formatReportFields(r) {
  if (!r) return '—';
  const parts = [];
  if (r.number) parts.push(`Report Number: ${r.number}`);
  if (r.revision) parts.push(`Revision: ${r.revision}`);
  if (r.date) parts.push(`Date: ${r.date}`);
  if (r.prepared_by) parts.push(`Prepared by: ${r.prepared_by}`);
  return parts.length ? parts.join('\n') : '—';
}

const BUILDERS_COMPLETION_TEXT =
  'All works have been carried out in accordance with the approved plans, specifications, all ' +
  'non-structural parts and their components and their fastenings (walls, partitions, ceilings, ' +
  'fixtures and fittings and the like) have been installed to achieve compliance with Section 8 of ' +
  'AS 1170.4-2007 and any deviations have been disclosed to the Principal Certifier.\n\n' +
  'I acknowledge that no products or materials were replaced based on the approved or relied upon ' +
  'documentation and/or correspondence to Incert Pty Ltd.';

export async function exportBuildersDeclarationPdf(d, project) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const companyInfo = getDefaultCompanyInfo();

  // ── Header ──────────────────────────────────────────────────────────────
  let y = await renderNavyHeader(doc, {
    logoUrl: companyInfo.logo_url || getLOGO_URL(),
    companyName: companyInfo.name,
    address: companyInfo.address,
    phone: companyInfo.phone,
    director: companyInfo.director,
    LOGO_FALLBACK_URL,
  });

  // ── Title block ─────────────────────────────────────────────────────────
  doc.setTextColor(...GOLD);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('INSTALLATION CERTIFICATE', PAGE.margin, y);
  y += 7;

  doc.setTextColor(...NAVY);
  doc.setFontSize(16);
  doc.text("Builder's Completion Certificate", PAGE.margin, y);
  y += 3;

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
  y += 6;

  // ── Certification text ──────────────────────────────────────────────────
  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  y = ensureSpace(doc, y, 5);
  doc.text('I hereby certify that:', PAGE.margin, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  y = addWrappedText(
    doc,
    '(a) The building work has been completed or inspected during construction and complies with the ' +
    'approved Construction Certificate / Complying Development Certificate documentation, plans, ' +
    'specifications, any other documents approved in writing by the Principal Certifier, and the ' +
    'nominated Standards of Performance listed below.',
    PAGE.margin, y, CONTENT_WIDTH, 4.5
  );
  y += 1;

  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...MID_GRAY);
  y = addWrappedText(
    doc,
    'The information contained in this statement is true and accurate to the best of my knowledge.',
    PAGE.margin, y, CONTENT_WIDTH, 4.5
  );
  doc.setTextColor(...TEXT_COLOR);
  y += 4;

  // ── Project Details ─────────────────────────────────────────────────────
  y = sectionBar(doc, y, 'Project Details');
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH / 3 } },
    body: [
      ['CC / CDC Number (Including Modifications)', d.cc_cdc_number || '—'],
      ['Development Consent No. (Including Modifications)', d.development_consent_no || '—'],
      ['Detail Description of The Building Part to be Certified (Whole / Part)', d.building_part_description || '—'],
      ['Development Address', d.development_address || '—'],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Standards of Performance ────────────────────────────────────────────
  y = sectionBar(doc, y, 'Standards of Performance');
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH * 0.3 },
      1: { cellWidth: CONTENT_WIDTH * 0.7 },
    },
    head: [['Measure and / or System', 'Standards of Performance']],
    body: [
      ["Builder's Completion", BUILDERS_COMPLETION_TEXT],
      ['Access Report', formatReportFields(d.access_report)],
      ['Fire Engineering Report', formatReportFields(d.fire_engineering_report)],
      ['Details of any exclusions or limitations (if any)', d.exclusions_limitations || '—'],
      ['Other Practices, Documents or Standards relied upon for this Certification', d.other_practices_documents || '—'],
      ['Copies attached (includes certificates from subcontractors/suppliers, manufacturers, test reports etc.)', d.copies_attached || '—'],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Certifying Builder ──────────────────────────────────────────────────
  y = sectionBar(doc, y, 'Certifying Builder');
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH / 3 } },
    body: [
      ['Full Name', d.builder_name || '—'],
      ['Company', d.builder_company || '—'],
      ['ABN', d.builder_abn || '—'],
      ['Phone', d.builder_phone || '—'],
      ['Address', d.builder_address || '—'],
      ['Licence / Accreditation / Registration Number', `${d.builder_licence || '—'}${d.builder_licence_type ? `  (${d.builder_licence_type})` : ''}`],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Signature & Date ─────────────────────────────────────────────────────
  y = ensureSpace(doc, y, 35);
  const sigBoxW = CONTENT_WIDTH * 0.55;
  const sigBoxH = 25;

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.rect(PAGE.margin, y, sigBoxW, sigBoxH);

  if (d.signature) {
    try {
      const sigImg = await loadImageAsBase64(d.signature);
      if (sigImg) {
        const props = doc.getImageProperties(sigImg);
        const ratio = props.width / props.height;
        let imgH = sigBoxH - 4;
        let imgW = imgH * ratio;
        if (imgW > sigBoxW - 4) {
          imgW = sigBoxW - 4;
          imgH = imgW / ratio;
        }
        doc.addImage(sigImg, 'PNG', PAGE.margin + 2, y + 2, imgW, imgH);
      }
    } catch (e) {
      console.warn('Signature embed failed:', e);
    }
  }

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_GRAY);
  doc.text('Signature', PAGE.margin, y + sigBoxH + 4);

  const dateX = PAGE.margin + sigBoxW + 10;
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.text('Date', dateX, y + 5);
  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'normal');
  doc.text(d.date ? formatDate(d.date) : '—', dateX, y + 11);

  y += sigBoxH + 8;

  // ── Footer ───────────────────────────────────────────────────────────────
  addFooters(doc, companyInfo.name);

  const projectName = project?.name || project?.project_name || 'Project';
  const fileName = `Builders_Declaration_${projectName}.pdf`.replace(/[\\/:*?"<>|]/g, '_');
  await savePDF(doc, fileName);
}
